import React, {useEffect, useRef, useState} from 'react';
import {orderAdminApi} from './orderAdminApi';
import styles from './OrderAdminDashboard.module.css';

export const SUPPLIER_MCAP_TASK = {id: 131, name: 'Biology Lab'};
const SUPPLIER_BATCH_SIZE = 25;
const SECONDS_PER_PARSED_EPISODE = 0.15;
const RETRY_DELAYS_MS = [2000, 5000, 15000];
const NOT_APPLICABLE = 'N/A';
const PENDING_STATUS = 'pending';
// Final per-episode outcomes. Anything else (pending, error, not_requested) is parsed again on resume.
const DONE_STATUSES = new Set([
    'ok', 'no_match', 'multiple_codes', 'no_task_info', 'no_mcap_path', 'mcap_file_not_found', 'read_limit_exceeded',
]);
const CHECKPOINT_DB = 'prismax-order-exports';
const CHECKPOINT_STORE = 'supplierTaskIdBatches';

const CSV_COLUMNS = [
    'episode_id', 'upload_id', 'task_id', 'task_name', 'job_id', 'job_name', 'source',
    'duration_hours', 'uploaded_at', 'admin_added_at', 'reported_bad',
    'supplier_task_id', 'supplier_raw_mcap_metadata', 'supplier_parse_status',
];

function csvCell(value) {
    const text = value == null ? '' : String(value);
    return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

function parseCsv(text) {
    const rows = [];
    let row = [];
    let cell = '';
    let quoted = false;
    for (let index = 0; index < text.length; index += 1) {
        const char = text[index];
        if (quoted) {
            if (char !== '"') cell += char;
            else if (text[index + 1] === '"') { cell += '"'; index += 1; }
            else quoted = false;
        } else if (char === '"') {
            quoted = true;
        } else if (char === ',') {
            row.push(cell);
            cell = '';
        } else if (char === '\n' || char === '\r') {
            if (char === '\r' && text[index + 1] === '\n') index += 1;
            row.push(cell);
            rows.push(row);
            row = [];
            cell = '';
        } else {
            cell += char;
        }
    }
    if (cell || row.length) {
        row.push(cell);
        rows.push(row);
    }
    return rows;
}

function metadataCell(metadata) {
    if (metadata == null) return '';
    return typeof metadata === 'string' ? metadata : JSON.stringify(metadata);
}

function isSupplierEpisode(episode) {
    return Number(episode.task_id) === SUPPLIER_MCAP_TASK.id;
}

function isDone(result) {
    return Boolean(result && DONE_STATUSES.has(result.status));
}

function supplierColumns(episode, parsed, parseRequested, missingStatus) {
    if (!isSupplierEpisode(episode)) {
        return {supplier_task_id: NOT_APPLICABLE, supplier_raw_mcap_metadata: NOT_APPLICABLE, supplier_parse_status: 'not_applicable'};
    }
    if (!parseRequested) {
        return {supplier_task_id: NOT_APPLICABLE, supplier_raw_mcap_metadata: NOT_APPLICABLE, supplier_parse_status: 'not_requested'};
    }
    const result = parsed[String(episode.episode_id)];
    if (!result) return {supplier_task_id: '', supplier_raw_mcap_metadata: '{}', supplier_parse_status: missingStatus};
    if (result.status === 'not_applicable') {
        return {supplier_task_id: NOT_APPLICABLE, supplier_raw_mcap_metadata: NOT_APPLICABLE, supplier_parse_status: 'not_applicable'};
    }
    return {
        supplier_task_id: result.supplier_task_id || '',
        supplier_raw_mcap_metadata: metadataCell(result.supplier_raw_mcap_metadata) || '{}',
        supplier_parse_status: result.status,
    };
}

function buildCsv(episodes, parsed, parseRequested, missingStatus = 'error') {
    const rows = episodes.map((episode) => ({
        episode_id: episode.episode_id,
        upload_id: episode.upload_id,
        task_id: episode.task_id,
        task_name: episode.task_name,
        job_id: episode.job_id,
        job_name: episode.job_name,
        source: episode.source,
        duration_hours: episode.duration_hours,
        uploaded_at: episode.uploaded_at,
        admin_added_at: episode.manually_added ? episode.admin_added_at : '',
        reported_bad: episode.reported_bad ? 'true' : 'false',
        ...supplierColumns(episode, parsed, parseRequested, missingStatus),
    }));
    return [CSV_COLUMNS, ...rows.map((row) => CSV_COLUMNS.map((column) => row[column]))]
        .map((cells) => cells.map(csvCell).join(','))
        .join('\r\n');
}

function exportFileName(order, suffix = '') {
    const today = new Date().toISOString().slice(0, 10).replaceAll('-', '');
    const name = String(order.name || '').trim().replace(/[^A-Za-z0-9_-]+/g, '_').replace(/^_+|_+$/g, '');
    return `${order.id}${name ? `_${name}` : ''}_episodes${suffix}_${today}.csv`;
}

function downloadCsv(fileName, csv) {
    const blob = new Blob([`﻿${csv}`], {type: 'text/csv;charset=utf-8'});
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
}

function parsePartialCsv(text, supplierEpisodeIds) {
    const [header = [], ...rows] = parseCsv(text.replace(/^﻿/, ''));
    const column = Object.fromEntries(header.map((name, index) => [name.trim(), index]));
    if (['episode_id', 'supplier_task_id', 'supplier_raw_mcap_metadata', 'supplier_parse_status'].some((name) => column[name] == null)) {
        throw new Error('This is not an episode CSV from this export.');
    }
    const fileIds = rows.map((row) => Number(row[column.episode_id])).filter((id) => Number.isSafeInteger(id) && id > 0);
    const orderIds = new Set(supplierEpisodeIds);
    const matched = fileIds.filter((id) => orderIds.has(id)).length;
    const fileSupplierIds = rows
        .filter((row) => row[column.supplier_parse_status] !== 'not_applicable')
        .map((row) => Number(row[column.episode_id]));
    if (!fileIds.length || (fileSupplierIds.length && matched / fileSupplierIds.length < 0.5)) {
        throw new Error('This CSV does not match this Order’s Episodes.');
    }
    const parsed = {};
    rows.forEach((row) => {
        const episodeId = Number(row[column.episode_id]);
        const status = row[column.supplier_parse_status];
        if (!orderIds.has(episodeId) || !DONE_STATUSES.has(status)) return;
        parsed[String(episodeId)] = {
            supplier_task_id: row[column.supplier_task_id] || null,
            supplier_raw_mcap_metadata: row[column.supplier_raw_mcap_metadata] || '{}',
            status,
        };
    });
    return parsed;
}

let checkpointDb = null;

function openCheckpointDb() {
    if (!checkpointDb) {
        checkpointDb = new Promise((resolve, reject) => {
            const request = window.indexedDB.open(CHECKPOINT_DB, 1);
            request.onupgradeneeded = () => {
                request.result.createObjectStore(CHECKPOINT_STORE, {autoIncrement: true}).createIndex('orderId', 'orderId');
            };
            request.onsuccess = () => resolve(request.result);
            request.onerror = () => reject(request.error);
        });
        checkpointDb.catch(() => { checkpointDb = null; });
    }
    return checkpointDb;
}

async function checkpointRequest(mode, run) {
    const db = await openCheckpointDb();
    return new Promise((resolve, reject) => {
        const transaction = db.transaction(CHECKPOINT_STORE, mode);
        const request = run(transaction.objectStore(CHECKPOINT_STORE));
        transaction.oncomplete = () => resolve(request?.result);
        transaction.onerror = () => reject(transaction.error);
    });
}

async function saveCheckpoint(orderId, results) {
    try {
        await checkpointRequest('readwrite', (store) => store.add({orderId, savedAt: Date.now(), results}));
    } catch {
        // Browser storage can be unavailable (private mode, quota). The partial CSV still protects progress.
    }
}

async function loadCheckpoint(orderId) {
    try {
        const records = await checkpointRequest('readonly', (store) => store.index('orderId').getAll(orderId)) || [];
        return {
            results: Object.assign({}, ...records.map((record) => record.results)),
            savedAt: Math.max(0, ...records.map((record) => record.savedAt)),
        };
    } catch {
        return {results: {}, savedAt: 0};
    }
}

async function clearCheckpoint(orderId) {
    try {
        await checkpointRequest('readwrite', (store) => {
            const request = store.index('orderId').openKeyCursor(IDBKeyRange.only(orderId));
            request.onsuccess = () => {
                if (!request.result) return;
                store.delete(request.result.primaryKey);
                request.result.continue();
            };
            return request;
        });
    } catch {
        // Nothing to clear when storage is unavailable.
    }
}

function currentAdminToken(fallback) {
    try {
        return localStorage.getItem('prismax_admin_token') || fallback;
    } catch {
        return fallback;
    }
}

const wait = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

export default function OrderEpisodeExportModal({adminToken, order, episodes, onClose, onNotify}) {
    const supplierEpisodes = episodes.filter(isSupplierEpisode);
    const supplierEpisodeIds = supplierEpisodes.map((episode) => Number(episode.episode_id));
    const canParse = supplierEpisodes.length > 0;
    const [parseMcap, setParseMcap] = useState(false);
    const [running, setRunning] = useState(false);
    const [progress, setProgress] = useState(0);
    const [error, setError] = useState('');
    const [notice, setNotice] = useState('');
    const [savedCheckpoint, setSavedCheckpoint] = useState(null);
    const [resumeSource, setResumeSource] = useState(null);
    const parsed = useRef({});
    const cancelled = useRef(false);
    const closedByUser = useRef(false);
    const stopRequested = useRef(false);
    const startedAt = useRef(0);
    const startedFrom = useRef(0);
    const partialFiles = useRef(0);
    const recoveredErrors = useRef(0);
    const fileInput = useRef(null);
    const doneCount = (results) => supplierEpisodeIds.filter((id) => isDone(results[String(id)])).length;
    const remainingAtStart = supplierEpisodes.length - doneCount(parsed.current);
    const estimateMinutes = Math.max(1, Math.ceil((remainingAtStart * SECONDS_PER_PARSED_EPISODE) / 60));
    const parsedThisRun = progress - startedFrom.current;
    const secondsPerEpisode = parsedThisRun > 0 ? (Date.now() - startedAt.current) / 1000 / parsedThisRun : SECONDS_PER_PARSED_EPISODE;
    const remainingMinutes = Math.max(1, Math.ceil(((supplierEpisodes.length - progress) * secondsPerEpisode) / 60));

    useEffect(() => {
        if (!canParse) return undefined;
        let active = true;
        loadCheckpoint(order.id).then((checkpoint) => {
            const count = supplierEpisodeIds.filter((id) => isDone(checkpoint.results[String(id)])).length;
            if (active && count) setSavedCheckpoint({...checkpoint, count});
        });
        return () => { active = false; };
        // Load once per opened Order; Episode changes do not reopen the modal.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [order.id, canParse]);

    useEffect(() => {
        if (!running) return undefined;
        const warn = (event) => {
            event.preventDefault();
            event.returnValue = '';
        };
        window.addEventListener('beforeunload', warn);
        return () => window.removeEventListener('beforeunload', warn);
    }, [running]);

    useEffect(() => () => { cancelled.current = true; }, []);

    const close = () => {
        closedByUser.current = true;
        cancelled.current = true;
        onClose();
    };

    const savePartial = () => {
        const done = doneCount(parsed.current);
        const fileName = exportFileName(order, `_PARTIAL_${done}of${supplierEpisodes.length}`);
        downloadCsv(fileName, buildCsv(episodes, parsed.current, true, PENDING_STATUS));
        partialFiles.current += 1;
        return fileName;
    };

    const resumeFromCheckpoint = () => {
        Object.assign(parsed.current, savedCheckpoint.results);
        setResumeSource(`browser progress (${savedCheckpoint.count.toLocaleString()} parsed)`);
        setSavedCheckpoint(null);
        setParseMcap(true);
    };

    const discardCheckpoint = () => {
        void clearCheckpoint(order.id);
        setSavedCheckpoint(null);
    };

    const resumeFromFile = async (file) => {
        if (!file) return;
        setError('');
        try {
            const results = parsePartialCsv(await file.text(), supplierEpisodeIds);
            Object.assign(parsed.current, results);
            void saveCheckpoint(order.id, results);
            setResumeSource(`${file.name} (${doneCount(parsed.current).toLocaleString()} parsed)`);
            setSavedCheckpoint(null);
            setParseMcap(true);
        } catch (fileError) {
            setError(fileError.message || 'Could not read that CSV.');
        } finally {
            if (fileInput.current) fileInput.current.value = '';
        }
    };

    const fetchBatch = async (batch) => {
        for (let attempt = 0; ; attempt += 1) {
            try {
                return await orderAdminApi.getEpisodeSupplierTaskIds(currentAdminToken(adminToken), batch) || {};
            } catch (requestError) {
                const retryable = !requestError.status || requestError.status >= 500;
                if (!retryable || attempt >= RETRY_DELAYS_MS.length || cancelled.current) throw requestError;
                recoveredErrors.current += 1;
                await wait(RETRY_DELAYS_MS[attempt]);
            }
        }
    };

    const exportCsv = async () => {
        const parseRequested = parseMcap && canParse;
        cancelled.current = false;
        stopRequested.current = false;
        setError('');
        setNotice('');
        if (parseRequested) {
            const todo = supplierEpisodeIds.filter((id) => !isDone(parsed.current[String(id)]));
            const alreadyDone = supplierEpisodes.length - todo.length;
            startedAt.current = Date.now();
            startedFrom.current = alreadyDone;
            setProgress(alreadyDone);
            setRunning(true);
            try {
                for (let offset = 0; offset < todo.length; offset += SUPPLIER_BATCH_SIZE) {
                    if (cancelled.current) {
                        if (!closedByUser.current) savePartial();
                        return;
                    }
                    if (stopRequested.current) {
                        const fileName = savePartial();
                        setNotice(`Stopped. Progress is saved in this browser and in ${fileName}. Use Resume or Continue from a partial CSV to finish.`);
                        setRunning(false);
                        return;
                    }
                    const batch = todo.slice(offset, offset + SUPPLIER_BATCH_SIZE);
                    const results = await fetchBatch(batch);
                    Object.assign(parsed.current, results);
                    void saveCheckpoint(order.id, results);
                    setProgress(alreadyDone + Math.min(todo.length, offset + batch.length));
                }
            } catch (parseError) {
                if (closedByUser.current) return;
                const fileName = savePartial();
                if (cancelled.current) return;
                setError(`${parseError.message || 'MCAP parsing stopped because of an error.'} Progress is saved in this browser and in ${fileName}. Use Resume or Continue from a partial CSV to finish.`);
                setSavedCheckpoint(null);
                setResumeSource(`browser progress (${doneCount(parsed.current).toLocaleString()} parsed)`);
                setRunning(false);
                return;
            }
            setRunning(false);
        }
        downloadCsv(exportFileName(order), buildCsv(episodes, parsed.current, parseRequested));
        void clearCheckpoint(order.id);
        const details = [];
        if (recoveredErrors.current) {
            details.push(`Recovered from ${recoveredErrors.current} temporary error${recoveredErrors.current === 1 ? '' : 's'}.`);
        }
        if (partialFiles.current) {
            details.push(`${partialFiles.current} partial file${partialFiles.current === 1 ? ' was' : 's were'} saved along the way; this final file replaces ${partialFiles.current === 1 ? 'it' : 'them'}.`);
        }
        onNotify('success', [`${order.id} CSV downloaded (${episodes.length.toLocaleString()} Episodes).`, ...details].join(' '));
        onClose();
    };

    return (
        <div className={styles.modalBackdrop} role="presentation">
            <section className={styles.exportModal} role="dialog" aria-modal="true" aria-labelledby="episode-export-title">
                <header>
                    <div>
                        <h2 id="episode-export-title">Download episode CSV</h2>
                        <p>{order.name ? `${order.name} · ` : ''}{order.id}</p>
                    </div>
                    <button type="button" aria-label="Close episode CSV download" onClick={close}>×</button>
                </header>
                <div className={styles.exportModalBody}>
                    <p className={styles.exportSummary}>
                        {episodes.length.toLocaleString()} Episode{episodes.length === 1 ? '' : 's'}
                        {' · '}
                        {supplierEpisodes.length.toLocaleString()} {SUPPLIER_MCAP_TASK.name} (Task {SUPPLIER_MCAP_TASK.id}) Episode{supplierEpisodes.length === 1 ? '' : 's'} eligible for MCAP parsing
                    </p>
                    {savedCheckpoint && !running ? (
                        <div className={styles.exportResume}>
                            <span>
                                Saved progress in this browser: {savedCheckpoint.count.toLocaleString()} / {supplierEpisodes.length.toLocaleString()} parsed
                                {savedCheckpoint.savedAt ? ` (${new Date(savedCheckpoint.savedAt).toLocaleString()})` : ''}
                            </span>
                            <div>
                                <button type="button" className={styles.primaryButton} onClick={resumeFromCheckpoint}>Resume</button>
                                <button type="button" onClick={discardCheckpoint}>Discard</button>
                            </div>
                        </div>
                    ) : null}
                    <label className={styles.exportOption}>
                        <input
                            type="checkbox"
                            checked={parseMcap && canParse}
                            disabled={!canParse || running}
                            onChange={(event) => setParseMcap(event.target.checked)}
                        />
                        <span>Parse MCAP files for supplier task IDs ({SUPPLIER_MCAP_TASK.name} only)</span>
                    </label>
                    {!canParse ? (
                        <p className={styles.exportHint}>No {SUPPLIER_MCAP_TASK.name} (Task {SUPPLIER_MCAP_TASK.id}) Episodes in this Order; MCAP parsing is unavailable. Supplier columns will show N/A.</p>
                    ) : parseMcap ? (
                        <p className={styles.exportHint}>
                            Reads only the /task_info metadata from each {SUPPLIER_MCAP_TASK.name} Episode, a few at a time.
                            {resumeSource ? ` Continuing from ${resumeSource}; ${remainingAtStart.toLocaleString()} left to parse.` : ''}
                            {remainingAtStart ? ` Estimated time: about ${estimateMinutes} minute${estimateMinutes === 1 ? '' : 's'}.` : ''}
                            {' '}Progress is saved as it goes. If parsing stops, a partial CSV downloads and you can resume later.
                        </p>
                    ) : (
                        <p className={styles.exportHint}>Supplier columns will show N/A for every Episode.</p>
                    )}
                    {canParse && !running ? (
                        <div className={styles.exportFileResume}>
                            <button type="button" onClick={() => fileInput.current?.click()}>Continue from a partial CSV…</button>
                            <input
                                ref={fileInput}
                                className={styles.exportFileInput}
                                type="file"
                                accept=".csv,text/csv"
                                onChange={(event) => void resumeFromFile(event.target.files?.[0])}
                            />
                        </div>
                    ) : null}
                    {running ? (
                        <div className={styles.exportProgress} role="status">
                            <strong>Parsing MCAP files {progress.toLocaleString()} / {supplierEpisodes.length.toLocaleString()} · about {remainingMinutes} min remaining</strong>
                            <span>Keep this tab open until the download starts. Progress is saved after every batch.</span>
                        </div>
                    ) : null}
                    {notice ? <p role="status" className={styles.exportNotice}>{notice}</p> : null}
                    {error ? <p role="alert" className={styles.editorError}>{error}</p> : null}
                </div>
                <footer>
                    {running ? (
                        <>
                            <button type="button" onClick={() => savePartial()}>Save progress</button>
                            <button type="button" onClick={() => { stopRequested.current = true; }}>Stop &amp; save progress</button>
                        </>
                    ) : (
                        <button type="button" onClick={close}>Cancel</button>
                    )}
                    <button type="button" className={styles.primaryButton} disabled={running} onClick={() => void exportCsv()}>
                        {running ? 'Parsing…' : 'Download CSV'}
                    </button>
                </footer>
            </section>
        </div>
    );
}
